import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useParams, useNavigate, Link } from "react-router-dom"
import "../style.scss"
import Loading from '../../../components/Loading/Index'
import Requests from '../../../utils/Requests/Index'
import ProfileHero from '../../../components/SingleProfile/ProfileHero/Index'
import ProfileDetails from '../../../components/SingleProfile/ProfileDetails/Index'
import PartnerPreference from '../../../components/SingleProfile/PartnerPreference/Index'
import { singleProContext } from '../../../utils/context/SingleProfileContext'
import { io } from "socket.io-client"
import Icon from 'react-icons-kit'
import {
    activity, alertCircle, bookOpen, briefcase, calendar, chevronLeft, chevronRight,
    droplet, globe, mapPin, users
} from 'react-icons-kit/feather'
import { HeightCalculate } from '../../../utils/helpers'
import { useSelector } from 'react-redux'

const Index = () => {
    const navigate = useNavigate()
    const { id } = useParams();
    const { ids } = useContext(singleProContext);
    const [key, setKey] = useState("details");
    const [data, setData] = useState({});
    const [isLoading, setLoading] = useState(true);
    const [header] = useState({ headers: { Authorization: "Bearer " + localStorage.getItem('token') } })
    const detailsRef = useRef(null)
    const partnerRef = useRef(null)
    const socket = useRef()
    const userInfo = useSelector((state) => state.user.userData)

    // Fetch data
    const fetchData = useCallback(async () => {
        setLoading(true)
        const response = await Requests.MyProfile.Single_Profile(id, header)
        if (response && response.user) setData(response.user)
        setLoading(false)
    }, [header, id]);

    useEffect(() => {
        fetchData()
    }, [header, fetchData, id]);

    useEffect(() => {
        socket.current = io(`${process.env.REACT_APP_API_URL}`, { transports: ["websocket", 'polling'] })
    }, [])

    useEffect(() => {
        if (!isLoading) {
            socket.current.emit("profileViewed", {
                id: userInfo?.userID,
                name: userInfo?.name,
                image: userInfo?.profilePicture,
                to: id
            })
        }
    }, [id, isLoading, userInfo])

    // Highlight the section currently in view
    useEffect(() => {
        const onScroll = () => {
            const partnerTop = partnerRef.current ? partnerRef.current.getBoundingClientRect().top : Infinity
            setKey(partnerTop <= 220 ? 'partner' : 'details')
        }
        window.addEventListener('scroll', onScroll, { passive: true })
        onScroll()
        return () => window.removeEventListener('scroll', onScroll)
    }, [data])

    // Profile prev & next Function..
    let currentIndex = ids?.indexOf(id) || 0;
    const prevProfile = () => ids?.[currentIndex - 1] && navigate(`/single-profile/${ids[currentIndex - 1]}`);
    const nextProfile = () => ids?.[currentIndex + 1] && navigate(`/single-profile/${ids[currentIndex + 1]}`);

    const goToSection = section => {
        setKey(section)
        const target = section === 'partner' ? partnerRef.current : detailsRef.current
        if (target) {
            const top = target.getBoundingClientRect().top + window.scrollY - 90
            window.scrollTo({ top, behavior: 'smooth' })
        }
    }

    if (isLoading) return <Loading />

    const glance = [
        { k: 'Age', v: data?.age ? `${data.age} Years` : null, icon: calendar },
        { k: 'Height', v: data?.height ? HeightCalculate(data.height) : null, icon: activity },
        { k: 'Marital status', v: data?.materialStatus, icon: users },
        { k: 'Religion', v: data?.religion, icon: globe },
        { k: 'Community / Order', v: data?.socialOrder, icon: globe },
        { k: 'Mother tongue', v: data?.language, icon: globe },
        { k: 'Blood group', v: data?.bloodGroup, icon: droplet },
        { k: 'Birth year', v: data?.birthYear, icon: calendar },
        { k: 'Lives in', v: [data?.city, data?.livingCountry].filter(Boolean).join(', '), icon: mapPin },
        { k: 'Working with', v: data?.workingWith, icon: briefcase },
        { k: 'Education', v: data?.qualification, icon: bookOpen },
    ].filter(row => row.v)

    if (!data?._id) {
        return (
            <div className="sp-page">
                <div className="container">
                    <div className="sp-empty">
                        <span className="sp-empty-icon"><Icon icon={alertCircle} size={26} /></span>
                        <h4>Profile unavailable</h4>
                        <p>This profile could not be loaded. It may have been removed or is no longer visible to you.</p>
                        <Link to="/matches/all" className="sp-empty-btn">Browse matches</Link>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="sp-page">
            <div className="container">

                {/* Top bar */}
                <div className="sp-topbar">
                    <div className="sp-topbar-text">
                        <h6 className="text-capitalize">{data?.name ? `${data.name}'s profile` : 'Profile'}</h6>
                        <p>Profile {(currentIndex || 0) + 1} of {ids?.length || 1} in your match list</p>
                    </div>

                    <div className="sp-nav">
                        <button
                            type="button"
                            disabled={currentIndex ? false : true}
                            onClick={prevProfile}
                        >
                            <Icon icon={chevronLeft} size={16} /> Previous
                        </button>
                        <button
                            type="button"
                            disabled={currentIndex >= (ids?.length - 1 || 0) ? true : false}
                            onClick={nextProfile}
                        >
                            Next <Icon icon={chevronRight} size={16} />
                        </button>
                    </div>
                </div>

                <div className="row g-4">
                    <div className="col-12 col-xl-8 col-xxl-9">
                        <ProfileHero item={data} />

                        {/* Section tabs */}
                        <div className="sp-tabs">
                            <button
                                type="button"
                                className={key === 'details' ? 'active' : ''}
                                onClick={() => goToSection('details')}
                            >
                                Profile Details
                            </button>
                            <button
                                type="button"
                                className={key === 'partner' ? 'active' : ''}
                                onClick={() => goToSection('partner')}
                            >
                                Partner Preference
                            </button>
                        </div>

                        <div ref={detailsRef} className="sp-anchor">
                            <ProfileDetails data={data} />
                        </div>

                        <div ref={partnerRef} className="sp-anchor">
                            <PartnerPreference data={data} />
                        </div>
                    </div>

                    {/* Sidebar */}
                    <aside className="col-12 col-xl-4 col-xxl-3">
                        <div className="sp-side">
                            {glance.length ?
                                <div className="sp-glance">
                                    <header className="sp-glance-head">
                                        <h5>At a Glance</h5>
                                        <p>Quick overview</p>
                                    </header>
                                    <div className="sp-glance-rows">
                                        {glance.map((row, i) =>
                                            <div className="sp-glance-row" key={i}>
                                                <span className="ic"><Icon icon={row.icon} size={15} /></span>
                                                <div>
                                                    <p className="k">{row.k}</p>
                                                    <p className="v">{row.v}</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                : null}

                            {data?.addsense ?
                                <div className="sp-ads">
                                    <img src={data.addsense} alt="Advertisement" />
                                </div>
                                : null}
                        </div>
                    </aside>
                </div>
            </div>
        </div>
    );
};

export default Index
